/**
 * Affiliate Sign Up Page - Legacy Triangle
 * Partner with us to earn commissions on funded deals
 */

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { 
  User, 
  Mail, 
  Phone, 
  Building2, 
  Globe,
  MessageSquare,
  CheckCircle,
  ArrowRight,
  DollarSign,
  Users,
  TrendingUp,
  Award
} from "lucide-react";

export default function Affiliate() {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    companyName: "",
    website: "",
    audienceType: "",
    monthlyReach: "",
    marketingChannels: [],
    hasAffiliateExperience: "",
    message: "",
    termsAccepted: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChannelChange = (channel) => {
    setFormData(prev => ({
      ...prev,
      marketingChannels: prev.marketingChannels.includes(channel)
        ? prev.marketingChannels.filter(c => c !== channel)
        : [...prev.marketingChannels, channel]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.termsAccepted) {
      alert("Please accept the terms and conditions to continue.");
      return;
    }
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          businessName: formData.companyName || 'N/A',
          consultationType: 'Affiliate Partner Application',
          fundingRange: formData.monthlyReach,
          yearsInBusiness: formData.hasAffiliateExperience,
          message: `Website: ${formData.website || 'N/A'}\nAudience Type: ${formData.audienceType}\nMarketing Channels: ${formData.marketingChannels.join(', ')}\n\nMessage: ${formData.message}`
        })
      });
      
      if (response.ok) {
        setSubmitSuccess(true);
      }
    } catch {
      // Form submission failed silently
    }
    
    setIsSubmitting(false);
  };

  const audienceTypes = [
    "Real Estate Investors",
    "Small Business Owners",
    "Entrepreneurs/Startups",
    "Financial Advisors/Consultants",
    "Business Coaches",
    "Social Media Influencers",
    "Accountants/CPAs",
    "Other"
  ];

  const reachOptions = [
    "Under 1,000",
    "1,000 - 5,000",
    "5,000 - 10,000",
    "10,000 - 50,000",
    "50,000+",
    "Not Sure"
  ];

  const marketingChannels = [
    "Email List",
    "Social Media",
    "YouTube/Video",
    "Blog/Website",
    "Podcast",
    "In-Person Networking",
    "Paid Advertising",
    "Other"
  ];

  const benefits = [
    { icon: DollarSign, title: "$500 - $2,500", desc: "Per funded deal" },
    { icon: TrendingUp, title: "No Cap", desc: "On your earnings" },
    { icon: Users, title: "Dedicated Support", desc: "Partner success team" },
    { icon: Award, title: "Monthly Payouts", desc: "Reliable payments" }
  ];

  if (submitSuccess) {
    return (
      <div className="min-h-screen bg-[#0f2d52]">
        <Navbar />
        <div className="min-h-[80vh] flex items-center justify-center px-4 pt-24">
          <div className="bg-white rounded-2xl p-8 md:p-12 max-w-lg text-center">
            <div className="w-20 h-20 rounded-full bg-[#48d480]/20 flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={40} className="text-[#48d480]" />
            </div>
            <h2 
              className="text-2xl md:text-3xl font-bold text-[#0f2d52] mb-4"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Application Received!
            </h2>
            <p className="text-gray-600 mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
              Thank you for your interest in becoming a Legacy Triangle affiliate partner. 
              Our team will review your application and contact you within 48 hours.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 bg-[#48d480] hover:bg-[#3bc070] text-white font-bold px-6 py-3 rounded-lg transition-colors"
            >
              Back to Home <ArrowRight size={18} />
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0f2d52]" data-testid="affiliate-page">
      <Navbar />

      {/* Hero Section */}
      <section className="pt-28 pb-8 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-[#e8821c]/20 rounded-full px-4 py-2 mb-6">
            <span className="w-2 h-2 rounded-full bg-[#e8821c]" />
            <span className="text-[#e8821c] text-sm font-medium">Partner Program</span>
          </div>
          
          <h1 
            className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Earn <span className="text-[#e8821c]">$500 - $2,500</span> Per Referral
          </h1>
          
          <p 
            className="text-white/70 text-lg max-w-2xl mx-auto"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            Join our affiliate program and earn generous commissions for every client you refer 
            that gets funded. No cap on earnings, monthly payouts, and dedicated partner support.
          </p>
        </div>
      </section>

      {/* Benefits Row */}
      <section className="pb-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <div key={idx} className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10 text-center">
                  <Icon size={24} className="text-[#e8821c] mx-auto mb-2" />
                  <div className="text-white font-bold" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {benefit.title}
                  </div>
                  <div className="text-white/50 text-sm">{benefit.desc}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="pb-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-8">
            
            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-[#1a3d5c] rounded-2xl p-6 md:p-8 border border-white/10">
                <h2 
                  className="text-xl md:text-2xl font-bold text-white mb-2"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  Become an <span className="text-[#e8821c]">Affiliate Partner</span>
                </h2>
                <p className="text-white/60 text-sm mb-6" style={{ fontFamily: "'Inter', sans-serif" }}>
                  Fill out the form below and our partner success team will reach out within 48 hours.
                </p>

                <form onSubmit={handleSubmit} className="space-y-5">
                  {/* First & Last Name */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        First Name <span className="text-[#e8821c]">*</span>
                      </label>
                      <div className="relative">
                        <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                        <input
                          type="text"
                          required
                          value={formData.firstName}
                          onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                          placeholder="John"
                          className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 pl-11 pr-4 text-white placeholder-white/40 focus:outline-none focus:border-[#e8821c] transition-colors"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Last Name <span className="text-[#e8821c]">*</span>
                      </label>
                      <div className="relative">
                        <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                        <input
                          type="text"
                          required
                          value={formData.lastName}
                          onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                          placeholder="Smith"
                          className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 pl-11 pr-4 text-white placeholder-white/40 focus:outline-none focus:border-[#e8821c] transition-colors"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Email & Phone */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Email Address <span className="text-[#e8821c]">*</span>
                      </label>
                      <div className="relative">
                        <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          placeholder="john@example.com"
                          className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 pl-11 pr-4 text-white placeholder-white/40 focus:outline-none focus:border-[#e8821c] transition-colors"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Phone Number <span className="text-[#e8821c]">*</span>
                      </label>
                      <div className="relative">
                        <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                        <input
                          type="tel"
                          required
                          value={formData.phone}
                          onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                          placeholder="(555) 123-4567"
                          className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 pl-11 pr-4 text-white placeholder-white/40 focus:outline-none focus:border-[#e8821c] transition-colors"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Company Name & Website */}
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Company Name <span className="text-white/40">(Optional)</span>
                      </label>
                      <div className="relative">
                        <Building2 size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                        <input
                          type="text"
                          value={formData.companyName}
                          onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                          placeholder="Your Company LLC"
                          className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 pl-11 pr-4 text-white placeholder-white/40 focus:outline-none focus:border-[#e8821c] transition-colors"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-white/80 text-sm font-medium mb-2">
                        Website / Social Media URL
                      </label>
                      <div className="relative">
                        <Globe size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                        <input
                          type="url"
                          value={formData.website}
                          onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                          placeholder="https://yourwebsite.com"
                          className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 pl-11 pr-4 text-white placeholder-white/40 focus:outline-none focus:border-[#e8821c] transition-colors"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Audience Type */}
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Primary Audience Type <span className="text-[#e8821c]">*</span>
                    </label>
                    <select
                      required
                      value={formData.audienceType}
                      onChange={(e) => setFormData({ ...formData, audienceType: e.target.value })}
                      className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#e8821c] transition-colors appearance-none cursor-pointer"
                      style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23e8821c'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
                    >
                      <option value="" className="bg-[#0f2d52]">Who do you primarily serve?</option>
                      {audienceTypes.map((type) => (
                        <option key={type} value={type} className="bg-[#0f2d52]">{type}</option>
                      ))}
                    </select>
                  </div>

                  {/* Monthly Reach */}
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Estimated Monthly Reach
                    </label>
                    <select
                      value={formData.monthlyReach}
                      onChange={(e) => setFormData({ ...formData, monthlyReach: e.target.value })}
                      className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 px-4 text-white focus:outline-none focus:border-[#e8821c] transition-colors appearance-none cursor-pointer"
                      style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%23e8821c'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '20px' }}
                    >
                      <option value="" className="bg-[#0f2d52]">Select estimated reach...</option>
                      {reachOptions.map((option) => (
                        <option key={option} value={option} className="bg-[#0f2d52]">{option}</option>
                      ))}
                    </select>
                  </div>

                  {/* Marketing Channels */}
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-3">
                      Marketing Channels You Use <span className="text-white/40">(Select all that apply)</span>
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {marketingChannels.map((channel) => (
                        <label 
                          key={channel} 
                          className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                            formData.marketingChannels.includes(channel) 
                              ? 'bg-[#e8821c]/20 border-[#e8821c]' 
                              : 'bg-[#0f2d52] border-white/20 hover:border-white/40'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={formData.marketingChannels.includes(channel)}
                            onChange={() => handleChannelChange(channel)}
                            className="sr-only"
                          />
                          <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                            formData.marketingChannels.includes(channel) 
                              ? 'bg-[#e8821c] border-[#e8821c]' 
                              : 'border-white/40'
                          }`}>
                            {formData.marketingChannels.includes(channel) && (
                              <CheckCircle size={12} className="text-white" />
                            )}
                          </div>
                          <span className="text-white/80 text-sm">{channel}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Affiliate Experience */}
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Do you have previous affiliate experience?
                    </label>
                    <div className="flex gap-4">
                      <label className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                        formData.hasAffiliateExperience === 'yes' 
                          ? 'bg-[#e8821c]/20 border-[#e8821c]' 
                          : 'bg-[#0f2d52] border-white/20 hover:border-white/40'
                      }`}>
                        <input
                          type="radio"
                          name="experience"
                          value="yes"
                          checked={formData.hasAffiliateExperience === 'yes'}
                          onChange={(e) => setFormData({ ...formData, hasAffiliateExperience: e.target.value })}
                          className="sr-only"
                        />
                        <span className="text-white font-medium">Yes</span>
                      </label>
                      <label className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                        formData.hasAffiliateExperience === 'no' 
                          ? 'bg-[#e8821c]/20 border-[#e8821c]' 
                          : 'bg-[#0f2d52] border-white/20 hover:border-white/40'
                      }`}>
                        <input
                          type="radio"
                          name="experience"
                          value="no"
                          checked={formData.hasAffiliateExperience === 'no'}
                          onChange={(e) => setFormData({ ...formData, hasAffiliateExperience: e.target.value })}
                          className="sr-only"
                        />
                        <span className="text-white font-medium">No</span>
                      </label>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <label className="block text-white/80 text-sm font-medium mb-2">
                      Why do you want to partner with us?
                    </label>
                    <div className="relative">
                      <MessageSquare size={18} className="absolute left-4 top-4 text-white/40" />
                      <textarea
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        placeholder="Tell us about your business, your audience, and why you'd be a great partner..."
                        rows={4}
                        className="w-full bg-[#0f2d52] border border-white/20 rounded-lg py-3 pl-11 pr-4 text-white placeholder-white/40 focus:outline-none focus:border-[#e8821c] transition-colors resize-none"
                      />
                    </div>
                  </div>

                  {/* Terms Acceptance */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="termsAccepted"
                      checked={formData.termsAccepted}
                      onChange={(e) => setFormData({ ...formData, termsAccepted: e.target.checked })}
                      className="mt-1 w-4 h-4 rounded border-white/20 bg-[#0f2d52] text-[#e8821c] focus:ring-[#e8821c]"
                    />
                    <label htmlFor="termsAccepted" className="text-white/60 text-sm">
                      I agree to the affiliate program <Link to="/terms-of-service" className="text-[#e8821c] hover:underline">terms and conditions</Link>. 
                      I understand commissions are paid after deals are funded and verified.
                    </label>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-[#e8821c] hover:bg-[#d67518] text-white font-bold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                    style={{ fontFamily: "'Inter', sans-serif" }}
                  >
                    {isSubmitting ? "Submitting..." : "Submit Application"}
                    <ArrowRight size={18} />
                  </button>

                  <p className="text-white/40 text-xs text-center">
                    By submitting, you agree to our <Link to="/privacy-policy" className="text-[#e8821c] hover:underline">Privacy Policy</Link>. 
                    We'll never share your information.
                  </p>
                </form>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* How It Works */}
              <div className="bg-[#1a3d5c] rounded-2xl p-6 border border-white/10">
                <h3 
                  className="text-lg font-bold text-white mb-4"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  How It <span className="text-[#e8821c]">Works</span>
                </h3>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#e8821c]/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#e8821c] font-bold text-sm">1</span>
                    </div>
                    <div>
                      <div className="text-white font-medium text-sm">Apply</div>
                      <div className="text-white/50 text-xs">Submit your application</div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#e8821c]/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#e8821c] font-bold text-sm">2</span>
                    </div>
                    <div>
                      <div className="text-white font-medium text-sm">Get Approved</div>
                      <div className="text-white/50 text-xs">We review within 48 hours</div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#e8821c]/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#e8821c] font-bold text-sm">3</span>
                    </div>
                    <div>
                      <div className="text-white font-medium text-sm">Refer Clients</div>
                      <div className="text-white/50 text-xs">Share your unique link</div>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#e8821c]/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#e8821c] font-bold text-sm">4</span>
                    </div>
                    <div>
                      <div className="text-white font-medium text-sm">Get Paid</div>
                      <div className="text-white/50 text-xs">Earn $500-$2,500 per deal</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Commission Structure */}
              <div className="bg-[#1a3d5c] rounded-2xl p-6 border border-white/10">
                <h3 
                  className="text-lg font-bold text-white mb-4"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  Commission <span className="text-[#e8821c]">Structure</span>
                </h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between items-center py-2 border-b border-white/10">
                    <span className="text-white/60">Funding under $50K</span>
                    <span className="text-[#e8821c] font-bold">$500</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-white/10">
                    <span className="text-white/60">$50K - $150K</span>
                    <span className="text-[#e8821c] font-bold">$1,000</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-white/10">
                    <span className="text-white/60">$150K - $500K</span>
                    <span className="text-[#e8821c] font-bold">$1,500</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-white/60">$500K+</span>
                    <span className="text-[#e8821c] font-bold">$2,500</span>
                  </div>
                </div>
              </div>

              {/* Support Info */}
              <div className="bg-gradient-to-br from-[#e8821c]/20 to-[#e8821c]/5 rounded-2xl p-6 border border-[#e8821c]/30">
                <h3 
                  className="text-lg font-bold text-white mb-2"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  Questions?
                </h3>
                <p className="text-white/70 text-sm mb-4">
                  Our partner success team is here to help you maximize your earnings.
                </p>
                <a 
                  href="mailto:partners@legacytriangle.com" 
                  className="text-[#e8821c] font-medium text-sm hover:underline"
                >
                  partners@legacytriangle.com
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
